'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ComponentProps } from 'react';

export type ActionsProps = ComponentProps<'div'>;

export const Actions = ({ className, children, ...props }: ActionsProps) => (
  <div className={cn('tw:inline-flex tw:items-center tw:gap-1 tw:rounded-[10px]', className)} {...props}>
    {children}
  </div>
);

export type ActionProps = ComponentProps<'button'> & {
  tooltip?: string;
  label?: string;
  tooltipOpen?: boolean;
};

// Keep these in sync with the Tailwind sizing classes below (`tw:w-6`, `tw:h-6`,
// `tw:[&_svg]:w-5`). Single source of truth for action button dimensions;
// there is no theme-driven override for these today.
const ACTION_BUTTON_SIZE_PX = 24;
const ACTION_ICON_SIZE_PX = 20;

// Inline !important beats host stylesheet !important, AND pins absolute px so
// host rem scaling (e.g. Magento's `html { font-size: 62.5% }`, which makes
// 1rem = 10px) doesn't shrink our Tailwind rem-based sizes. Pinning the svg
// here because `tw:[&_svg]:w-5` resolves to 1.25rem and collapses to 12.5px
// on such hosts.
const forceActionButtonLayout = (el: HTMLButtonElement | null) => {
  if (!el) return;
  el.style.setProperty('width', `${ACTION_BUTTON_SIZE_PX}px`, 'important');
  el.style.setProperty('height', `${ACTION_BUTTON_SIZE_PX}px`, 'important');
  el.style.setProperty('padding', '0', 'important');
  el.style.setProperty('border', 'none', 'important');
  el.style.setProperty('box-sizing', 'border-box', 'important');
  const svg = el.querySelector('svg');
  if (svg) {
    svg.style.setProperty('width', `${ACTION_ICON_SIZE_PX}px`, 'important');
    svg.style.setProperty('height', `${ACTION_ICON_SIZE_PX}px`, 'important');
    svg.style.setProperty('flex-shrink', '0', 'important');
  }
};

export const Action = ({ tooltip, children, label, className, tooltipOpen, ...props }: ActionProps) => {
  const [hovered, setHovered] = useState(false);

  // When an explicit `tooltipOpen=true` (e.g. "Response copied!" confirmation)
  // closes, reset the internal hover flag so the hover tooltip doesn't
  // immediately re-open while the cursor is still on the button. The user has
  // to leave and re-enter for the hover tooltip to reappear.
  const prevTooltipOpenRef = useRef<boolean | undefined>(tooltipOpen);
  useEffect(() => {
    if (prevTooltipOpenRef.current === true && !tooltipOpen) {
      setHovered(false);
    }
    prevTooltipOpenRef.current = tooltipOpen;
  }, [tooltipOpen]);

  const isOpen = tooltipOpen || hovered;

  const button = (
    <button
      ref={forceActionButtonLayout}
      className={cn(
        'tw:flex tw:items-center tw:justify-center tw:w-6 tw:h-6 tw:min-w-0 tw:min-h-0 tw:cursor-pointer tw:rounded-[5px] tw:bg-transparent tw:border-none tw:p-0 tw:text-current tw:hover:bg-[#F7F8FA] tw:transition-colors tw:[&_svg]:w-5 tw:[&_svg]:h-5 tw:[&_svg]:shrink-0',
        className,
      )}
      type="button"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...props}
    >
      {children}
      <span className="tw:sr-only">{label || tooltip}</span>
    </button>
  );

  if (tooltip) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip open={isOpen}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
};
